'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { trackCTAClick } from '@/lib/analytics';
import Link from 'next/link';

interface PricingPlan {
  id: string;
  tag: string;
  name: string;
  price: string;
  currency: string;
  price_type: string;
  priceNote: string;
  description: string;
  features: string[];
  cta: string;
  featured: boolean;
  badge: string;
  is_promotion: boolean;
  promotional_price: string;
  promotion_end_date: string;
}

interface PakietyContent {
  title: string;
  subtitle: string;
}

interface CountdownTimerProps {
  endDate: string;
  featured: boolean;
}

function CountdownTimer({ endDate, featured }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    expired: false,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(endDate).getTime() - new Date().getTime();
      
      if (difference <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
        expired: false,
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  if (timeLeft.expired) {
    return null;
  }

  return (
    <div className={`mt-4 p-4 rounded-lg border-2 ${
      featured 
        ? 'bg-white/10 border-white/20' :'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200'
    }`}>
      <div className={`text-xs font-700 uppercase tracking-wide mb-2 ${
        featured ? 'text-white' : 'text-orange-600'
      }`} style={{ fontFamily: 'Inter, sans-serif' }}>
        Promocja kończy się za:
      </div>
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center">
          <div className={`text-2xl font-800 ${
            featured ? 'text-white' : 'text-orange-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            {timeLeft.days}
          </div>
          <div className={`text-xs font-500 ${
            featured ? 'text-slate-200' : 'text-gray-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            {timeLeft.days === 1 ? 'dzień' : 'dni'}
          </div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-800 ${
            featured ? 'text-white' : 'text-orange-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            {String(timeLeft.hours).padStart(2, '0')}
          </div>
          <div className={`text-xs font-500 ${
            featured ? 'text-slate-200' : 'text-gray-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            godz
          </div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-800 ${
            featured ? 'text-white' : 'text-orange-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <div className={`text-xs font-500 ${
            featured ? 'text-slate-200' : 'text-gray-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            min
          </div>
        </div>
        <div className="text-center">
          <div className={`text-2xl font-800 ${
            featured ? 'text-white' : 'text-orange-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <div className={`text-xs font-500 ${
            featured ? 'text-slate-200' : 'text-gray-600'
          }`} style={{ fontFamily: 'Inter, sans-serif' }}>
            sek
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PakietySection() {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [content, setContent] = useState<PakietyContent>({
    title: 'Wybierz zakres wdrożenia.',
    subtitle: 'Każde wdrożenie poprzedza bezpłatna konsultacja. Ceny są orientacyjne.',
  });
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch packages
        const { data: packagesData, error: packagesError } = await supabase
          .from('packages')
          .select('*')
          .eq('enabled', true)
          .order('display_order', { ascending: true });

        if (packagesError) {
          console.error('Error fetching packages:', packagesError.message);
        } else {
          const formattedPlans: PricingPlan[] =
            packagesData?.map((pkg) => ({
              id: pkg.id,
              tag: pkg.tag || '',
              name: pkg.name,
              price: pkg.price,
              currency: pkg.currency || 'PLN',
              price_type: pkg.price_type || 'jednorazowo',
              priceNote: pkg.price_note || '',
              description: pkg.description,
              features: pkg.features || [],
              cta: pkg.cta || 'Zapytaj o wycenę',
              featured: pkg.featured || false,
              badge: pkg.badge || 'brak',
              is_promotion: pkg.is_promotion || false,
              promotional_price: pkg.promotional_price || '',
              promotion_end_date: pkg.promotion_end_date || '',
            })) || [];

          setPlans(formattedPlans);
        }

        // Fetch content
        const { data: settingsData, error: settingsError } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['pakiety_title', 'pakiety_subtitle']);

        if (settingsError) {
          console.error('Error fetching pakiety content:', settingsError.message);
        } else {
          const settingsMap: Record<string, string> = {};
          settingsData?.forEach((setting) => {
            settingsMap[setting.key] = setting.value;
          });

          setContent({
            title: settingsMap['pakiety_title'] || content.title,
            subtitle: settingsMap['pakiety_subtitle'] || content.subtitle,
          });
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [supabase]);

  if (loading) {
    return (
      <section id="pakiety" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <p className="text-muted">Ładowanie pakietów...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="pakiety" className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 text-center space-y-3">
          <p className="text-xs font-700 uppercase tracking-wide text-blue-DEFAULT" style={{ fontFamily: 'Inter, sans-serif' }}>Pakiety</p>
          <h2 className="text-4xl md:text-5xl font-700 tracking-tight text-navy leading-[1.0]" style={{ fontFamily: 'Inter, sans-serif' }}>
            {content.title}
          </h2>
          <p className="text-base text-muted font-400 max-w-lg mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            {content.subtitle}
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans?.map((plan, index) => (
            <PackageCard key={plan.id} plan={plan} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PackageCard({ plan, index }: { plan: PricingPlan; index: number }) {
  const { elementRef, isVisible } = useScrollAnimation();

  const handlePackageInquiry = () => {
    trackCTAClick(plan.cta, `pakiety_section_${plan.name}`);
  };

  // Check if promotion is active
  const isPromotionActive = plan.is_promotion && plan.promotional_price && 
    (!plan.promotion_end_date || new Date(plan.promotion_end_date) > new Date());

  // Check if this is the Online package for special styling
  const isOnlinePackage = plan.name === 'Online';

  return (
    <div
      id={`package-${plan.name.toLowerCase()}`}
      ref={elementRef}
      className={`pricing-card transition-all duration-700 ease-out hover:scale-105 hover:shadow-2xl ${
        plan.featured ? 'featured' : ''
      } ${
        isOnlinePackage ? 'border-2 border-teal-400 bg-gradient-to-br from-teal-50 to-cyan-50' : ''
      } ${
        isVisible
          ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: `${index * 150}ms`,
      }}
    >
      {/* Badge */}
      <div className="flex items-center justify-between">
        {isPromotionActive && (
          <span
            className="text-xs font-800 uppercase tracking-wide bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            PROMOCJA
          </span>
        )}
        {!isPromotionActive && plan.badge !== 'brak' && (
          <span
            className={`text-xs font-800 uppercase tracking-wide ${
              isOnlinePackage ? 'text-teal-600' : plan.featured ? 'text-blue-light' : 'text-blue-DEFAULT'
            }`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {plan.badge}
          </span>
        )}
        {!isPromotionActive && plan.badge === 'brak' && plan.tag && (
          <span
            className={`text-xs font-800 uppercase tracking-wide ${
              isOnlinePackage ? 'text-teal-600' : plan.featured ? 'text-blue-light' : 'text-blue-DEFAULT'
            }`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {plan.tag}
          </span>
        )}
      </div>

      {/* Name */}
      <h3 className={`text-3xl font-700 mt-4 flex items-center gap-3 ${
        isOnlinePackage ? 'text-teal-700' : 'text-navy'
      }`} style={{ fontFamily: 'Inter, sans-serif' }}>
        {plan.name}
        {isOnlinePackage && (
          <div className="relative inline-flex items-center">
            <Icon
              name="CloudIcon"
              size={32}
              className="text-teal-500"
              variant="outline"
            />
            <div className="absolute bottom-0 right-0 bg-white rounded-full p-0.5">
              <Icon
                name="LockClosedIcon"
                size={14}
                className="text-teal-600"
                variant="solid"
              />
            </div>
          </div>
        )}
      </h3>

      {/* Price */}
      <div className="mt-3">
        {isPromotionActive ? (
          <>
            <div className="flex items-baseline gap-2">
              <div className={`text-4xl font-800 ${
                isOnlinePackage ? 'text-teal-600' : plan.featured ? 'text-white' : 'text-orange-500'
              }`} style={{ fontFamily: 'Inter, sans-serif' }}>
                {plan.promotional_price} {plan.currency}
              </div>
            </div>
            <div className={`text-lg font-600 line-through mt-1 ${
              plan.featured ? 'text-slate-300' : 'text-gray-400'
            }`} style={{ fontFamily: 'Inter, sans-serif' }}>
              {plan.price} {plan.currency}
            </div>
            <div className={`text-sm font-500 mt-1 ${
              plan.featured ? 'text-slate-100' : 'text-muted'
            }`} style={{ fontFamily: 'Inter, sans-serif' }}>
              {plan.priceNote || plan.price_type}
            </div>
          </>
        ) : (
          <>
            <div className={`text-4xl font-800 ${
              plan.featured ? 'text-white' : 'text-navy'
            }`} style={{ fontFamily: 'Inter, sans-serif' }}>
              {plan.price} {plan.currency}
            </div>
            <div className={`text-sm font-500 mt-1 ${
              plan.featured ? 'text-slate-100' : 'text-muted'
            }`} style={{ fontFamily: 'Inter, sans-serif' }}>
              {plan.priceNote || plan.price_type}
            </div>
          </>
        )}
      </div>

      {/* Countdown Timer */}
      {isPromotionActive && plan.promotion_end_date && (
        <CountdownTimer endDate={plan.promotion_end_date} featured={plan.featured} />
      )}

      {/* Description */}
      <p className={`text-sm font-400 leading-relaxed mt-4 ${
        plan.featured ? 'text-white' : 'text-muted'
      }`} style={{ fontFamily: 'Inter, sans-serif' }}>
        {plan.description}
      </p>

      {/* Features */}
      <ul className="space-y-3 mt-6">
        {plan.features?.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <Icon
              name="CheckCircleIcon"
              size={20}
              className={isOnlinePackage ? 'text-teal-500' : 'text-blue-DEFAULT'}
              variant="solid"
            />
            <span className={`text-sm font-400 ${
              plan.featured ? 'text-white' : 'text-navy'
            }`} style={{ fontFamily: 'Inter, sans-serif' }}>{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA - Use Link for Online package, anchor for others */}
      {isOnlinePackage ? (
        <Link
          href="/zamow-ksef"
          className={`block w-full text-center py-3 px-6 rounded-xl font-800 text-sm mt-8 transition-all duration-300 ease-out active:scale-95 bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 hover:scale-105 hover:shadow-lg`}
          style={{ fontFamily: 'Inter, sans-serif' }}
          onClick={handlePackageInquiry}
        >
          {plan.cta}
        </Link>
      ) : (
        <a
          href="#kontakt"
          className={`block w-full text-center py-3 px-6 rounded-xl font-800 text-sm mt-8 transition-all duration-300 ease-out active:scale-95 ${
            plan.featured
              ? 'bg-blue-DEFAULT text-white hover:bg-blue-light hover:scale-105 hover:shadow-lg' :'bg-white border-2 border-navy text-navy hover:bg-navy hover:text-white hover:scale-105 hover:shadow-lg'
          }`}
          style={{ fontFamily: 'Inter, sans-serif' }}
          onClick={handlePackageInquiry}
        >
          {plan.cta}
        </a>
      )}
    </div>
  );
}